import type {
  AvailabilityMap,
  Contributor,
  DateAvailability,
  DateAvailabilityMap,
  ExperienceLevelMap,
  Solution } from
'../types';
import { getAdobeAccessToken, getAuthenticatedUserId } from '../auth/adobeIms';
import {
  normalizeHydratedSolutions } from
'./solutionOrder';
import {
  normalizeHydratedContributors } from
'./contributorOrder';

// Base URL for Adobe IO Runtime actions
const DB_ACTION_BASE_URL = "https://55338-856salmonanaconda-stage.adobeioruntime.net/api/v1/web/ondemand-tracker";
// const IMS_ORG_ID = "@AdobeOrg"

export interface UserRecord {
  email: string;
  name: string | null;
  accessLevel: AdobeImsAccessLevel;
}

export async function fetchUserRecord(email: string): Promise<UserRecord | null> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('db-get-user'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token || ''}`
      },
      // body: ""
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (!data?.found) {
      return null;
    }

    return data.user as UserRecord;
  } catch (error) {
    console.error('Error fetching user record:', error);
    return null;
  }
}

export interface DashboardStatePayload {
  contributors: Contributor[];
  solutions: Solution[];
  availability: AvailabilityMap;
  dateAvailability: DateAvailabilityMap;
  experienceLevels: ExperienceLevelMap;
}

function actionUrl(action: string): string {
  if (!DB_ACTION_BASE_URL) {
    throw new Error('Missing DB_ACTION_BASE_URL');
  }
  return `${DB_ACTION_BASE_URL.replace(/\/$/, '')}/${action}`;
}



function authHeaders(): Record<string, string> {
  const token = getAdobeAccessToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token || ''}`,
    // 'Authorization': `Bearer 1234567890`,
    // 'x-gw-ims-org-id': IMS_ORG_ID
  };
}

async function callAction(
  action: string,
  body: Record<string, any>
): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const response = await fetch(actionUrl(action), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`Action "${action}" failed with status ${response.status}`);
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error(`Error calling action "${action}":`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

export async function fetchInitialStateFromDatabase():
Promise<DashboardStatePayload | null> {
  try {
    // const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('db-get-state'), {
      method: 'GET',
      // headers1: {
      //   'Accept': 'application/json',
      //   'Authorization': `Bearer ${token || ''}`
      // }
      headers: authHeaders()
    });

    if (!response.ok) {
      if (response.status === 403) {
        console.warn(
          'Initial dashboard state was not returned (403). User not registered.'
        );
        return null;
      }
      throw new Error(`Failed to fetch initial state (${response.status})`);
    }

    const payload = await response.json();
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    // Action returns { contributors, solutions, availability, dateAvailability, experienceLevels }
    const { contributors, solutions, availability, dateAvailability, experienceLevels } = payload;

    if (!Array.isArray(contributors) || !Array.isArray(solutions)) {
      return null;
    }

    return {
      contributors: normalizeHydratedContributors(contributors as Contributor[]),
      solutions: normalizeHydratedSolutions(solutions as Solution[]),
      availability: (availability || {}) as AvailabilityMap,
      dateAvailability: (dateAvailability || {}) as DateAvailabilityMap,
      experienceLevels: (experienceLevels || {}) as ExperienceLevelMap
    };
  } catch (error) {
    console.error('Error fetching initial state from Database:', error);
    return null;
  }
}

type SolutionRecordUpdate =
Pick<Solution, 'id'> &
Partial<Omit<Solution, 'id'>>;

type ContributorOrderRecord =
Pick<Contributor, 'id' | 'displayOrder'>;

type SolutionOrderRecord =
Pick<Solution, 'id' | 'displayOrder'>;

type DateAvailabilityRecord =
DateAvailability &
{
  contributorId: string;
  solutionId: string;
};

export async function upsertContributorRecord(
contributor: Contributor)
{
  return callAction('db-update-contributor', {
    operation: 'upsert',
    contributor,
    
  });
}

export async function deleteContributorRecord(
contributor: Pick<Contributor, 'id' | 'name' | 'region'>)
{
  return callAction('db-update-contributor', {
    operation: 'delete',
    contributor,
    
  });
}

export async function upsertSolutionRecord(
solution: SolutionRecordUpdate)
{
  return callAction('db-update-solution', {
    operation: 'upsert',
    solution,
    
  });
}

export async function deleteSolutionRecord(
solution: Pick<Solution, 'id'> & Partial<Pick<Solution, 'name'>>)
{
  return callAction('db-update-solution', {
    operation: 'delete',
    solution,
    
  });
}

export async function reorderContributorRecords(
contributors: ContributorOrderRecord[])
{
  return callAction('db-reorder-contributors', {
    contributors,
    
  });
}

export async function reorderSolutionRecords(
solutions: SolutionOrderRecord[])
{
  return callAction('db-reorder-solutions', {
    solutions,
    actorId: getAuthenticatedUserId() || 'anonymous'
  });
}

export async function addDateAvailabilityRecord(
record: DateAvailabilityRecord,
contributor?: Pick<Contributor, 'name' | 'region'>,
solution?: Pick<Solution, 'name'>)
{
  return callAction('db-manage-date-availability', {
    operation: 'add',
    record,
    contributorName: contributor?.name || null,
    region: contributor?.region || null,
    solutionName: solution?.name || null,
    actorId: getAuthenticatedUserId() || 'anonymous'
  });
}

export async function deleteDateAvailabilityRecord(
record: Partial<DateAvailabilityRecord> & Pick<DateAvailabilityRecord, 'id'>,
contributor?: Pick<Contributor, 'name' | 'region'>,
solution?: Pick<Solution, 'name'>)
{
  return callAction('db-manage-date-availability', {
    operation: 'delete',
    record,
    contributorName: contributor?.name || null,
    region: contributor?.region || null,
    solutionName: solution?.name || null,
    
  });
}

export async function logAvailabilityChange(
contributor: {id: string;name: string;region: string;},
solution: {id: string;name: string;},
status: string,
experienceLevel?: string)
{
  return callAction('db-update-availability', {
    contributorId: contributor.id,
    contributorName: contributor.name,
    region: contributor.region,
    solutionId: solution.id,
    solutionName: solution.name,
    status,
    experienceLevel: experienceLevel || null,
    
  });
}

export async function logExperienceLevelChange(
contributor: {id: string;name: string;region: string;},
solution: {id: string;name: string;},
experienceLevel: string | undefined,
status: string)
{
  return callAction('db-update-experience', {
    contributorId: contributor.id,
    contributorName: contributor.name,
    region: contributor.region,
    solutionId: solution.id,
    solutionName: solution.name,
    level: experienceLevel || null,
    status,
    
  });
}

// export async function logOOOSchedule(
// contributor: {id: string;name: string;region: string;},
// startDate: string,
// endDate: string,
// isClearing: boolean = false)
// {
//   // OOO data persisted via logStateToDatabase (useEffect full-state backup).
//   // This call records the audit trail entry.
//   return callAction('db-log-activity', {
//     actionType: isClearing ? 'OOO Cleared' : 'OOO Schedule',
//     contributorId: contributor.id,
//     contributorName: contributor.name,
//     region: contributor.region,
//     startDate: isClearing ? null : startDate,
//     endDate: isClearing ? null : endDate,
//     notes: isClearing
//       ? 'Out of office cleared'
//       : `Out of office from ${startDate} to ${endDate}`,
//     actorId: getAuthenticatedUserId() || 'anonymous',
//     timestamp: new Date().toISOString()
//   });
// }

// export async function logDateOverride(
// contributor: {id: string;name: string;region: string;},
// solution: {id: string;name: string;},
// startDate: string,
// endDate: string,
// status: string,
// isDeleting: boolean = false)
// {
//   // Date availability records are persisted via logStateToDatabase (useEffect full-state backup).
//   // This call records the audit trail entry.
//   return callAction('db-log-activity', {
//     actionType: isDeleting ? 'Date Override Deleted' : 'Date Override Added',
//     contributorId: contributor.id,
//     contributorName: contributor.name,
//     region: contributor.region,
//     solutionId: solution.id,
//     solutionName: solution.name,
//     status,
//     startDate,
//     endDate,
//     notes: isDeleting
//       ? `Removed scheduled ${status} from ${startDate} to ${endDate}`
//       : `Scheduled ${status} from ${startDate} to ${endDate}`,
//     actorId: getAuthenticatedUserId() || 'anonymous',
//     timestamp: new Date().toISOString()
//   });
// }

// export async function logRegionUpdate(
// contributor: {id: string;name: string;region: string;},
// newRegion: string)
// {
//   return callAction('db-log-activity', {
//     actionType: 'Region Update',
//     contributorId: contributor.id,
//     contributorName: contributor.name,
//     region: newRegion,
//     notes: `Region changed from ${contributor.region} to ${newRegion}`,
//     actorId: getAuthenticatedUserId() || 'anonymous',
//     timestamp: new Date().toISOString()
//   });
// }

// export async function logContributorEdit(
// contributorId: string,
// oldName: string,
// newName: string,
// oldRegion: string,
// newRegion: string)
// {
//   return callAction('db-log-activity', {
//     actionType: 'Contributor Edited',
//     contributorId,
//     contributorName: newName,
//     region: newRegion,
//     notes: `Name: ${oldName} → ${newName}, Region: ${oldRegion} → ${newRegion}`,
//     actorId: getAuthenticatedUserId() || 'anonymous',
//     timestamp: new Date().toISOString()
//   });
// }

export async function logSolutionEdit(
solutionId: string,
_oldName: string,
newName: string,
displayOrder?: number)
{
  // Update the solution record and write audit log.
  return callAction('db-update-solution', {
    operation: 'upsert',
    solution: {
      id: solutionId,
      name: newName,
      ...(typeof displayOrder === 'number' ? { displayOrder } : {})
    },
    
  });
}

// submitToDatabase is kept for any legacy call sites not yet migrated.
// It routes to db-log-activity as a generic audit log entry.
export async function submitToDatabase(
data: Record<string, any>)
: Promise<{success: boolean;error?: string;}> {
  const actorId = getAuthenticatedUserId() || 'anonymous';
  const payload = { ...data, actorId };
  return callAction('db-log-activity', payload);
}
